// ─── Shared furniture for the Assistant tabs ─────────────────────────────────
//
// Seven tabs were each inventing their own card: three different border
// radii, four padding values, headings from 12px to 14px. Nothing was WRONG
// on its own, and together it read as seven pages built by seven people.
//
// ⚠️ THESE ARE THE ONLY CARD AND HEADING IN THE ASSISTANT AREA. A tab that
// needs something different should get a variant here, not a one-off class
// string — the one-off is exactly how the drift started.
//
// Values follow the existing Storiz tokens (`web/src/lib/brand.ts`): warm
// neutrals, mustard accent, semantic colours kept separate from the accent so
// "late" can never look like "selected".
import { useId, useState, type ReactNode } from 'react';

/* ── Icons ──────────────────────────────────────────────────────────────────
 *
 * ⚠️ SVG, NOT EMOJI. The board shipped with ⏸ and 🔴 as status markers on the
 * customer cards: they render differently on every platform, cannot inherit
 * currentColor, and ignore the theme entirely. Same 1.7 stroke as the icons in
 * AdminAssistantPage so the whole area shares one visual language.
 */
type IconProps = { className?: string; title?: string };

const Svg = ({ children, className = 'w-3.5 h-3.5', title }: IconProps & { children: ReactNode }) => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round"
    className={`${className} shrink-0`}
    role={title ? 'img' : undefined} aria-hidden={title ? undefined : true} aria-label={title}
  >
    {title && <title>{title}</title>}
    {children}
  </svg>
);

/** An open complaint. Deliberately not a red dot alone — colour is never the
 *  only carrier of meaning. */
export const AlertIcon = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" /></Svg>
);
/** A human has the conversation. */
export const PauseIcon = (p: IconProps) => (
  <Svg {...p}><path d="M9 5v14M15 5v14" /></Svg>
);
export const CheckIcon = (p: IconProps) => <Svg {...p}><path d="M20 6 9 17l-5-5" /></Svg>;
export const InboxIcon = (p: IconProps) => (
  <Svg {...p}><path d="M3 12h5l2 3h4l2-3h5" /><path d="M5 5h14l2 7v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5z" /></Svg>
);

/* ── Surfaces ────────────────────────────────────────────────────────────── */

/** The one card. `pad={false}` when the content manages its own padding
 *  (a list whose rows need full-bleed dividers). */
/** The disclosure chevron. Same 1.7 stroke and 24-viewBox as every other icon
 *  in this file, so a row of them reads as one family. */
export const ChevronIcon = (p: IconProps) => (
  <Svg {...p}><path d="M6 9l6 6 6-6" /></Svg>
);

/**
 * One closed line showing what a group of settings is DOING. Click to open.
 *
 * ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * The Numbers tab had 25 controls stacked vertically, every one of them
 * visible. Owner: "I have to read everything to know anything." The state is
 * what gets checked twenty times a day; the controls are what get touched once
 * a month. This inverts which of the two is on screen.
 *
 * ⚠️ IT HIDES CONTROLS, IT NEVER REMOVES THEM. Every setting stays exactly one
 * click away, and the summary must say enough that opening it is a choice
 * rather than a search.
 *
 * ⚠️ DO NOT USE FOR ONE SETTING. A row that opens to reveal a single checkbox
 * is strictly worse than the checkbox. Nor for anything urgent — errors and
 * warnings stay visible, which is why `tone="error"` forces this open.
 *
 * ─── EVERY VALUE COMES FROM THE ASSISTANT TABS AS THEY ARE ──────────────────
 *
 * Container, padding, type sizes and focus ring are lifted from `Card` /
 * `CardHeader` above and from the 12 existing `focus:ring-brand-500` uses.
 * ⚠️ Deliberately NOT from `lib/brand.ts` — those tokens are warm browns and
 * cream, and these tabs are built in plain Tailwind greys. Matching the tokens
 * would visibly restyle the page, which this pass is explicitly not doing.
 */
export function SettingGroup({
  title, summary, children, defaultOpen = false, tone = 'default', loading = false, onToggle,
}: {
  title: string;
  /** A node, not a string — summaries carry coloured spans ("on" in green,
   *  the RIB warning in amber) and colour must never be the only carrier. */
  summary: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  tone?: 'default' | 'sensitive' | 'error';
  loading?: boolean;
  onToggle?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  // An error is not something to go looking for behind a click.
  const isOpen = tone === 'error' ? true : open;
  const panelId = useId();

  return (
    <div className={`bg-white rounded-xl border ${tone === 'error' ? 'border-red-300' : 'border-gray-200'}`}>
      <button
        type="button"
        // ⚠️ A REAL BUTTON. A div with onClick is not reachable by keyboard and
        // announces nothing; `aria-expanded` is what tells a screen reader this
        // row has more behind it.
        aria-expanded={isOpen}
        aria-controls={panelId}
        disabled={tone === 'error'}
        onClick={() => { const next = !open; setOpen(next); onToggle?.(next); }}
        className={'w-full px-4 py-3 flex items-center justify-between gap-3 text-left rounded-xl '
          + 'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 '
          + (tone === 'error' ? 'cursor-default' : 'hover:bg-gray-50')}
      >
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-gray-800 leading-tight">{title}</span>
          <span className={'block text-[11.5px] mt-0.5 leading-snug '
            + (tone === 'error' ? 'text-red-600' : tone === 'sensitive' ? 'text-amber-700' : 'text-gray-500')}>
            {loading ? '—' : summary}
          </span>
        </span>
        {tone !== 'error' && (
          <ChevronIcon
            className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* ⚠️ `grid-template-rows: 0fr → 1fr` rather than an animated height.
          There is no height-animation pattern anywhere in this codebase to
          copy, and this one needs no measurement, no ResizeObserver, and no
          hardcoded max-height that silently clips a long panel.
          `prefers-reduced-motion` is already handled globally in index.css. */}
      <div
        id={panelId}
        className={`grid transition-[grid-template-rows] duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden">
          {/* Mounted only when open, so a closed panel contributes nothing to
              the tab order — hiding with CSS alone leaves focus able to walk
              into content nobody can see. */}
          {isOpen && (
            <div className="px-4 pb-4 pt-1 border-t border-gray-100">{children}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Card({ children, className = '', pad = true }: {
  children: ReactNode; className?: string; pad?: boolean;
}) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${pad ? 'p-4' : ''} ${className}`}>
      {children}
    </div>
  );
}

/** A card's header row. `right` takes a count, a hint, or a control. */
export function CardHeader({ title, right, sub }: {
  title: string; right?: ReactNode; sub?: string;
}) {
  return (
    <div className="px-4 py-3 border-b border-gray-100 flex items-baseline justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-[13px] font-semibold text-gray-800 leading-tight">{title}</h3>
        {sub && <p className="text-[11.5px] text-gray-500 mt-0.5 leading-snug">{sub}</p>}
      </div>
      {right && <span className="text-[11px] text-gray-400 tabular-nums shrink-0">{right}</span>}
    </div>
  );
}

/** A heading for a group of cards, outside any card. */
export function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 mb-2">
      <h3 className="text-[13px] font-semibold text-gray-800">{title}</h3>
      {hint && <span className="text-[11px] text-gray-400 hidden sm:inline">{hint}</span>}
    </div>
  );
}

/**
 * Nothing here — and why, plus what to do about it.
 *
 * ⚠️ AN EMPTY LIST IS NOT AN ERROR AND MUST NOT LOOK LIKE ONE. It is also not
 * nothing: a blank panel leaves the reader unsure whether it is empty or
 * broken. Say which.
 */
export function EmptyState({ children, icon = true }: { children: ReactNode; icon?: boolean }) {
  return (
    <div className="px-4 py-8 flex flex-col items-center gap-2 text-center">
      {icon && <InboxIcon className="w-5 h-5 text-gray-300" />}
      <p className="text-[12px] text-gray-400 max-w-xs leading-snug">{children}</p>
    </div>
  );
}

/** A horizontal bar in a ranked list. `tone` picks semantic vs accent. */
export function RankBar({ label, value, max, tone = 'accent', labelWidth = 'w-[92px]' }: {
  label: string; value: number; max: number; tone?: 'accent' | 'danger'; labelWidth?: string;
}) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2.5 text-[11.5px]">
      <span className={`${labelWidth} shrink-0 text-gray-600 truncate`} title={label}>{label}</span>
      <span className="flex-1 h-[7px] bg-gray-100 rounded-full overflow-hidden">
        <span
          className={`block h-full rounded-full transition-[width] duration-500 ${
            tone === 'danger' ? 'bg-red-400' : 'bg-brand-400'
          }`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <b className="w-7 text-right tabular-nums font-semibold text-gray-700">{value}</b>
    </div>
  );
}
